import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createAutomationClient, type AutomationClient, type AutomationClientConfig } from '../client'

interface AutomationContextValue {
  client: AutomationClient
  baseUrl: string
}

const AutomationContext = createContext<AutomationContextValue | null>(null)

export interface AutomationProviderProps {
  children: ReactNode
  /**
   * Base URL for the automation API
   * @example "http://localhost:4000"
   */
  baseUrl: string
  /**
   * Optional custom headers to include with every request
   * Useful for authentication tokens
   */
  headers?: Record<string, string>
  /**
   * Optional error handler for API errors
   */
  onError?: (error: Error) => void
  /**
   * Optional custom QueryClient instance
   * If not provided, a default one will be created
   */
  queryClient?: QueryClient
}

const defaultQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes (reduced API calls)
      gcTime: 1000 * 60 * 30, // 30 minutes (keep in cache longer)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

/**
 * Provider component that initializes the automation data layer.
 * Wrap your application with this provider to enable all automation hooks.
 *
 * @example
 * ```tsx
 * import { AutomationProvider } from '@dain/automation-data'
 *
 * function App() {
 *   return (
 *     <AutomationProvider
 *       baseUrl="http://localhost:4000"
 *       headers={{ Authorization: `Bearer ${token}` }}
 *     >
 *       <YourApp />
 *     </AutomationProvider>
 *   )
 * }
 * ```
 */
export function AutomationProvider({
  children,
  baseUrl,
  headers,
  onError,
  queryClient = defaultQueryClient,
}: AutomationProviderProps) {
  const config: AutomationClientConfig = useMemo(
    () => ({ baseUrl, headers, onError }),
    [baseUrl, headers, onError]
  )

  const client = useMemo(() => createAutomationClient(config), [config])

  const value = useMemo(() => ({ client, baseUrl }), [client, baseUrl])

  return (
    <QueryClientProvider client={queryClient}>
      <AutomationContext.Provider value={value}>{children}</AutomationContext.Provider>
    </QueryClientProvider>
  )
}

/**
 * Hook to access the automation client directly.
 * Use this for custom queries or mutations not covered by the built-in hooks.
 *
 * @example
 * ```tsx
 * const { client } = useAutomationClient()
 * const data = await client.traces.get(traceId)
 * ```
 */
export function useAutomationClient(): AutomationContextValue {
  const context = useContext(AutomationContext)
  if (!context) {
    throw new Error('useAutomationClient must be used within an AutomationProvider')
  }
  return context
}
